import { IPaymentDetail } from "pages/dashboard/wallets/interface"
import { useEffect, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { formatCurrencyNoSymbol } from "utils/currency"
import TotalEmployee from "assets/images/total_employee.svg"
import TotalSalary from "assets/images/total_salary.svg"
import EmployeeLeave from "assets/images/employee_leave.svg"
import { PayoutCard } from "../Component/PayoutCard"
import { Dropdown, Input, Spin, Tag, Tooltip } from "antd"
import { saveAs } from "file-saver"
import {
  ChevronDownIcon,
  EllipsisHorizontalIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"
import { useAppNotificationStore, useAuthStore } from "stores"
import { Button, P2 } from "components"
import getIdFromKey from "utils/getIdFromKey"
import {
  ICurrencyInfo,
  IPaymentScheduleItem,
  IPayScheduleItemDocsEntity,
  IPayScheduleItemRes,
} from "./interface/payouts"
import ProcessPayment from "./payouts/ProcessPayment"
import ViewPayoutModal from "../modals/ViewPayoutsModal"
import ReversePayment from "../modals/ReversePayment"
import UnreversePayment from "../modals/UnreversePayment"
import usePermissions from "hooks/usePermissions"
import RestorePayment from "../modals/RestorePayment"
import DeletePayment from "../modals/DeletePayment"
import RefreshPayment from "../modals/RefreshPayment"
import RetryPayment from "../modals/RetryPayment"
import UpdatePayment from "../modals/UpdatePayment"
import ResetPayment from "../modals/ResetPayment"
import ResponsiveActinBtn from "components/new-stuff/ui/ResponsiveActinBtn"
import SelectPayment from "../modals/SelectPayment"
import DeselectPayment from "../modals/DeselectPayment"
import DownloadPayment from "./payouts/DownloadPayment"
import { SelectedTable } from "../SelectedTable"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import { antIcon } from "pages/dashboard/dashboard/Dashboard"
import { ICurrencies } from "pages/dashboard/wallets/integration/interface/kyc"
import ViewTransferDetails from "pages/employee-dashboard/wallet/wallet/crossBorder/transfers/ViewTransferDetails"
import { ICustomScheduleApprovers } from "../Custom/interface"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { IFlowSetupRes } from "pages/dashboard/wallets/crossBorderPayments/Overview"
import StatutoryInfo from "pages/dashboard/new-company-onboarding/payroll-enabled/company/StatutoryInfo"
import { ColumnsType } from "antd/es/table"
import {
  PAYMENT_SCHEDULES_ENDPOINTS,
  paymentSchedulesService,
} from "services/paymentSchedulesService"
import { onQueryError } from "utils/utils"
import { useGet } from "utils/useFetch"
import {
  ONBOARDING_ENDPOINTS,
  onboardingService,
} from "services/onboardingService"
import { useBackendTableQuery } from "pages/dashboard/performance/components/useBackendTableQuery"

// EWA Repayment, Subscription Fee, External Loan Repayment, Subscription VAT Fee
const SYSTEM_ITEM_TYPE_IDS = [8, 11, 12, 13]

const ViewCurrency = ({
  activeCurrency,
  currencies,
}: {
  activeCurrency: ICurrencyInfo | null
  currencies: IBaseRes<ICurrencies[]> | undefined
}) => {
  const myParam = useLocation().search
  const x = new URLSearchParams(myParam).get("x")
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { toast } = useAppNotificationStore()
  const queryClient = useQueryClient()
  const [canProcess, canDelete] = usePermissions({
    allowedPermissions: [
      "can_process_pay_schedules",
      "can_delete_pay_schedules",
    ],
  })
  // USESTATES
  const [type, setType] = useState(0)
  const [processSelected, setProcessSelected] = useState(false)
  const [active, setActive] = useState<IPaymentScheduleItem | null>(null)
  const [paymentId, setPaymentId] = useState("")
  const [allSelectedRowSum, setAllSelectedRowSum] = useState(0)
  // ACTION STATES
  const [showViewPayout, setShowViewPayout] = useState(false)
  const [showApprovers, setShowApprovers] = useState(false)
  const [isStatutorySetup, setIsStatutorySetup] = useState(false)

  const [openSelectAllConfirmation, setOpenSelectAllConfirmation] =
    useState(false)
  const [openDeselectAllConfirmation, setOpenDeselectAllConfirmation] =
    useState(false)
  const [openReverseConfirmation, setOpenReverseConfirmation] = useState(false)
  const [openUnreverseConfirmation, setOpenUnreverseConfirmation] =
    useState(false)
  const [openResetConfirmation, setOpenResetConfirmation] = useState(false)
  const [openUpdateConfirmation, setOpenUpdateConfirmation] = useState(false)
  const [openRefreshConfirmation, setOpenRefreshConfirmation] = useState(false)
  const [openRetryConfirmation, setOpenRetryConfirmation] = useState(false)
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false)
  const [openDownloadConfirmation, setOpenDownloadConfirmation] =
    useState(false)
  const [activeReference, setActiveReference] = useState("")
  const [showDetails, setShowDetails] = useState(false)
  const [, setFilteredData] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState(0)

  // END OF ACTION STATES

  const {
    isLoading: isPayoutLoading,
    data: payoutData,
    search,
    handleSearchChange,
    handlePaginationChange,
    setQueryExtraParams,
  } = useBackendTableQuery<IPayScheduleItemRes>({
    queryKeyBase: [PAYMENT_SCHEDULES_ENDPOINTS.SELECT_PAYOUT_ITEMS],
    queryFn: (params) => paymentSchedulesService.selectPayoutItems(params),
    getTotal: (response) => response?.data?.totalDocs || 0,
    searchParamName: "searchParams",
    initialPageSize: 10,
    initialExtraParams: {
      id: getIdFromKey(x || ""),
      x: activeCurrency?.currency_code,
    },
    enabled: activeCurrency !== null,
    onSuccess: (data: IPayScheduleItemRes) => {
      setFilteredData(data?.data?.docs || [])
    },
  })

  useEffect(() => {
    setQueryExtraParams({
      id: getIdFromKey(x || ""),
      x: activeCurrency?.currency_code,
    })
  }, [x, activeCurrency?.currency_code])

  const { isLoading: isPaymentLoading, data } = useQuery<
    IBaseRes<IPaymentDetail>
  >({
    queryFn: () =>
      paymentSchedulesService.viewPaymentSchedules(
        "detail",
        x,
        activeCurrency?.currency_code
      ),
    queryKey: [
      PAYMENT_SCHEDULES_ENDPOINTS.VIEW_PAYMENT_SCHEDULES,
      x,
      activeCurrency?.currency_code,
    ],
    enabled: activeCurrency !== null ? true : false,
  })

  const companyId = user?.currentCompanyId
  const organisationId = user?.userOrganisationId

  const { data: isWalletSetup, isLoading: isLoadingSetupInfo } =
    useQuery<IFlowSetupRes>({
      queryFn: () => onboardingService.getSetupInfo(companyId, organisationId),
      queryKey: [
        ONBOARDING_ENDPOINTS.GET_SETUP_INFO,
        companyId,
        organisationId,
      ],
      enabled: !!companyId && !!organisationId,
    })

  const invalidateQueries = () => {
    setPaymentId(getIdFromKey(x || ""))
    queryClient.invalidateQueries({
      queryKey: [PAYMENT_SCHEDULES_ENDPOINTS.SELECT_PAYOUT_ITEMS],
    })
    queryClient.invalidateQueries({
      queryKey: [PAYMENT_SCHEDULES_ENDPOINTS.VIEW_PAYMENT_SCHEDULES],
    })
  }
  const {
    isLoading: isSubmittingDeselectionLoading,
    mutate: submitDeselection,
  } = useMutation({
    mutationFn: async (allSelectedIds: any) => {
      const data = await paymentSchedulesService.updateScheduleItems(
        "deselect",
        activeCurrency?.currency_code,
        allSelectedIds
      )
      return data
    },
    onSuccess: (data) => {
      invalidateQueries()
      mixPanelEvent("pay-schedule-payout-deselect-success")
      data?.message && toast.success(data.message)
    },
    onError: onQueryError,
  })

  const { isLoading: isSubmittingLoading, mutate: submitSelection } =
    useMutation({
      mutationFn: async (allSelectedIds: any) => {
        const data = await paymentSchedulesService.updateScheduleItems(
          "select",
          activeCurrency?.currency_code,
          allSelectedIds
        )

        return data
      },
      onSuccess: (data) => {
        // process()
        mixPanelEvent("pay-schedule-payout-select-success")
        invalidateQueries()
        data?.message && toast.success(data.message)
      },
      onError: onQueryError,
    })

  const cards = [
    {
      title: "Schedule Amount ",
      value: data?.data?.amount
        ? formatCurrencyNoSymbol(Number(data?.data?.amount))
        : 0,
      icon: TotalEmployee,
      id: "payment-schedule-amount",
    },
    {
      title: "Payout Amount",
      value: data?.data?.processed_amount
        ? formatCurrencyNoSymbol(Number(data?.data?.processed_amount))
        : 0,
      icon: TotalSalary,
      id: "payment-amount-processed",
    },
    {
      title: "Pending Payout",
      value: data?.data?.process_amount
        ? formatCurrencyNoSymbol(Number(data?.data?.process_amount))
        : 0,
      icon: EmployeeLeave,
      id: "payment-amount-to-process",
    },
    {
      typeId: 1,
      title: `Primary Account (${
        data?.data?.bank_count ? data?.data?.bank_count : 0
      })`,
      value: data?.data?.bank_amount
        ? formatCurrencyNoSymbol(Number(data?.data?.bank_amount))
        : 0,
      icon: EmployeeLeave,
      id: "payment-bank-account",
      handleClick: () => {
        if (type == 1) {
          setType(0)
        } else {
          setType(1)
        }
      },
    },

    {
      typeId: 2,
      title: `Secondary Account (${
        data?.data?.wallet_count ? data?.data?.wallet_count : 0
      })`,
      value: data?.data?.wallet_amount
        ? formatCurrencyNoSymbol(Number(data?.data?.wallet_amount))
        : 0,
      icon: TotalEmployee,
      id: "payment-custom-wallet",
      handleClick: () => {
        if (type == 2) {
          setType(0)
        } else {
          setType(2)
        }
      },
    },
  ]
  const uniqueBankNames = [
    ...new Set(payoutData?.data?.docs?.map((pay: any) => pay.bank_name)),
  ]
  const uniqueStatusNames = [
    ...new Set(
      payoutData?.data?.docs?.map(
        (pay: any) => pay.payment_schedule_item_status
      )
    ),
  ]
  const normalizedBankNames = uniqueBankNames?.map(
    (bankName) => bankName ?? "Unspecified"
  )

  const distinctBankNames = [...new Set(normalizedBankNames)]
  const sortedBankNames = distinctBankNames.sort((a, b) => {
    if (a === "Unspecified") return -1
    if (b === "Unspecified") return 1
    return a.localeCompare(b)
  })
  const bankFilters = sortedBankNames.map((bankName) => ({
    text: bankName,
    value: bankName,
  }))
  const statusFilters = uniqueStatusNames?.map((status) => ({
    text: status,
    value: status,
  }))
  const uniqueCurrencies = [
    ...new Set(payoutData?.data?.docs?.map((pay: any) => pay.currency_code)),
  ]

  const currencyFilters = uniqueCurrencies?.map((bankName) => ({
    text: bankName,
    value: bankName,
  }))

  const { isLoading: isLoadingNotes, data: approversData } = useQuery<
    IBaseRes<ICustomScheduleApprovers>
  >({
    queryFn: () => paymentSchedulesService.viewScheduleDetails(x),
    queryKey: [PAYMENT_SCHEDULES_ENDPOINTS.VIEW_SCHEDULE_DETAILS, x],
    enabled: data?.data?.payment_schedule_type_id !== 1 ? true : false,
  })

  const TableColumns: ColumnsType<IPayScheduleItemDocsEntity> = [
    {
      title: "Beneficiary",
      dataIndex: "beneficiary",
      render: (_, record: IPayScheduleItemDocsEntity) => (
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <Link
              to={
                record?.contractor_key
                  ? `/dashboard/contractor/view?x=${record?.contractor_key}`
                  : `/dashboard/employee/${record?.person_key}/job-and-pay`
              }
              onClick={(e) => {
                if (Number(record.payment_schedule_item_type_id) > 1) {
                  e.preventDefault()
                }
              }}
              className="flex items-center gap-2 hover:!text-primary500"
            >
              <span
                className={`font-circular text-sm  text-padeBlack ${
                  Number(record.payment_schedule_item_type_id) === 1 &&
                  "underline underline-offset-4"
                }
                ${
                  record?.payment_schedule_item_status_id === 1 &&
                  "text-success800"
                }
                  `}
              >
                {Number(record?.payment_schedule_item_type_id) > 1
                  ? record?.beneficiary_name || ""
                  : record?.full_name || record?.beneficiary_name || ""}
              </span>
            </Link>
            {Number(record.payment_schedule_item_type_id) > 1 && (
              <Tag
                color="processing"
                className="w-fit text-xs font-medium"
              >
                {record.payment_schedule_item_type_id == 3
                  ? "Pension Schedule"
                  : record.payment_schedule_item_type_id == 4
                  ? "NHF Schedule"
                  : record.payment_schedule_item_type_id == 6
                  ? "NHIS Schedule"
                  : record.payment_schedule_item_type_id == 2
                  ? "Tax Schedule"
                  : record.payment_schedule_item_type_id == 8
                  ? "EWA Repayment"
                  : record.payment_schedule_item_type_id == 9
                  ? "NSITF Schedule"
                  : record.payment_schedule_item_type_id == 11
                  ? "Subscription Fee"
                  : record.payment_schedule_item_type_id == 12
                  ? "External Loan Repayment"
                  : record.payment_schedule_item_type_id == 13
                  ? "Subscription VAT Fee"
                  : record.payment_schedule_item_type_id == 10
                  ? "ITF Schedule"
                  : ""}
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Account",
      dataIndex: "bank",
      width: "25%",
      filters:
        activeCurrency?.currency_code === "NGN" ? bankFilters : undefined,
      onFilter: (value: boolean | Key, record: IPayScheduleItemDocsEntity) => {
        const recordBank = record?.bank_name ?? "Unspecified"
        return recordBank === value
      },

      render: (_, record: IPayScheduleItemDocsEntity) => {
        return (
          <>
            {record?.source_currency_code === "USD" &&
            record?.account_number ? (
              <div className="flex flex-col mp-mask">
                <span
                  className={`font-circular flex flex-wrap text-sm text-padeBlack`}
                >
                  {record?.routing_code}
                  {record?.routing_type ? `(${record?.routing_type})` : "-"}
                </span>
                <span
                  className={`font-circular flex flex-wrap text-xs text-padeBlack`}
                >
                  {record.account_number}
                </span>
              </div>
            ) : record?.currency_code === "NGN" && record?.bank_name ? (
              <div className="flex flex-col mp-mask">
                <span
                  className={`font-circular flex flex-wrap text-sm text-padeBlack`}
                >
                  {record?.bank_name}
                </span>
                <span
                  className={`font-circular flex flex-wrap text-xs text-[#0c1b3d80]`}
                >
                  {record.account_number}
                </span>
              </div>
            ) : record?.currency_code !== "NGN" && record?.account_number ? (
              <div className="flex flex-col mp-mask">
                {record?.bank_name ? (
                  <span
                    className={`font-circular flex flex-wrap text-sm text-padeBlack`}
                  >
                    {record?.bank_name}
                  </span>
                ) : (
                  <span
                    className={`font-circular flex flex-wrap text-sm text-padeBlack`}
                  >
                    {record?.routing_code}
                    {record?.routing_type ? `(${record?.routing_type})` : "-"}
                    {}
                  </span>
                )}
                <span
                  className={`font-circular flex flex-wrap text-xs text-padeBlack`}
                >
                  {record.account_number}
                </span>
              </div>
            ) : (
              <Tooltip title="Employee is missing details required to run payroll. ">
                <div className="flex gap-2 items-center">
                  <ExclamationTriangleIcon className="h-6 w-6 text-danger500" />
                  <span>Unspecified</span>
                </div>
              </Tooltip>
            )}
          </>
        )
      },
    },
    {
      title: "Currency",
      dataIndex: "currency",
      filters: currencyFilters,
      onFilter: (value: any, record: any) => {
        return record?.currency_code === value
      },
      render: (_: any, record: IPayScheduleItemDocsEntity) => {
        const flag =
          currencies?.data?.find((item) => item.code === record.currency_code)
            ?.flagUrl || ""
        return (
          <div className="flex items-center gap-2">
            <div>
              <img
                src={flag}
                alt=""
                className="h-6 w-6"
              />
            </div>
            <span
              className={`font-circular flex flex-wrap text-sm text-padeBlack`}
            >
              {record?.currency_code}
            </span>
          </div>
        )
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (_, record: IPayScheduleItemDocsEntity) => (
        <div className="flex flex-col">
          <span
            className={`font-circular text-sm  text-padeBlack mp-mask
                ${
                  record.payment_schedule_item_status_id == 1 &&
                  "text-success800"
                }
                  `}
          >
            {activeCurrency?.currency_symbol ?? (
              <ExclamationTriangleIcon className=" h-6 w-6 text-danger500" />
            )}
            {formatCurrencyNoSymbol(Number(record?.amount))}{" "}
          </span>
          {Number(record.processing_fee) > 0 ? (
            <Tag
              color="processing"
              className=" text-xs font-medium mp-mask"
            >
              Fee: {activeCurrency?.currency_symbol}
              {formatCurrencyNoSymbol(Number(record.processing_fee))}
            </Tag>
          ) : (
            ""
          )}
          {!record?.is_active && (
            <Tag
              color="red"
              className=" text-xs font-medium w-fit"
            >
              Deleted
            </Tag>
          )}
        </div>
      ),
    },

    {
      title: "Payment Ref.No",
      dataIndex: "ref",

      render: (_, record: IPayScheduleItemDocsEntity) => (
        <div>
          <span
            className={`font-circular flex flex-wrap text-sm text-padeBlack`}
          >
            {record?.reference_number ?? "-"}
          </span>
          {record?.payment_method && <Tag>{record?.payment_method}</Tag>}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      filters: statusFilters,
      onFilter: (value: boolean | Key, record: IPayScheduleItemDocsEntity) => {
        const filterValue = String(value)
        return record?.payment_schedule_item_status === filterValue
      },
      render: (_, record: IPayScheduleItemDocsEntity) => (
        <div className="">
          <div className="flex items-center gap-1">
            <Tag
              color={
                record.payment_schedule_item_status_id === 1
                  ? "green"
                  : record.payment_schedule_item_status_id === 2
                  ? "orange"
                  : record.payment_schedule_item_status_id === 3
                  ? "red"
                  : record.payment_schedule_item_status_id === 4
                  ? "red"
                  : "default"
              }
            >
              {record?.payment_schedule_item_status}
            </Tag>
            {record?.response_description &&
              record.payment_schedule_item_status_id === 4 && (
                <Tooltip title={record.response_description}>
                  <InformationCircleIcon className="w-4 h-4" />
                </Tooltip>
              )}
          </div>
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record: IPayScheduleItemDocsEntity) => {
        const items = actionItems(record)
        return (
          <>
            {Number(record?.payment_schedule_item_type_id) == 8 ||
            !items.length ? (
              ""
            ) : (
              <Dropdown
                menu={{
                  items,
                }}
              >
                <button>
                  <EllipsisHorizontalIcon className="w-6 h-6 text-primary500" />
                </button>
              </Dropdown>
            )}
          </>
        )
      },
    },
  ]
  const actionItems = (record: IPayScheduleItemDocsEntity) => {
    const id = Number(record?.payment_schedule_item_status_id)
    const paymentid = record?.payment_schedule_item_id
    const selected = record?.is_selected
    const active = record?.is_active
    const payment_method_id = record?.payment_method_id
    const payment_schedule_item_type_id = record?.payment_schedule_item_type_id
    const isSystemItem = SYSTEM_ITEM_TYPE_IDS.includes(
      Number(payment_schedule_item_type_id)
    )
    const isPayout = Number(payment_schedule_item_type_id) === 1
    const paymentSuccessful =
      record?.response_description === "Transaction was successful" ||
      record?.response_description === "Transaction successful" ||
      record?.response_description ===
        "Transaction with reference already exists"
        ? true
        : false
    let arrayOptions: any = []

    {
      payment_method_id == 1 &&
        (id === 1 || id === 4) &&
        active &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "1",
            label: (
              <div
                // onClick={() => {
                //   setActive(record)
                //   setShowViewPayout(true)
                // }}
                onClick={() => {
                  setActiveReference(record?.reference_number)
                  setShowDetails(true)
                }}
              >
                {" "}
                View
              </div>
            ),
          },
        ])
    }
    {
      id == 1 &&
        active &&
        payment_schedule_item_type_id === 1 &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "2",
            label: (
              <div
                onClick={() => {
                  setPaymentId(String(paymentid))
                  setOpenReverseConfirmation(true)
                }}
              >
                {" "}
                Reverse
              </div>
            ),
          },
        ])
    }
    {
      id == 3 &&
        active &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "3",
            label: (
              <div
                onClick={() => {
                  setPaymentId(String(paymentid))
                  setOpenUnreverseConfirmation(true)
                }}
              >
                {" "}
                Unreverse
              </div>
            ),
          },
        ])
    }
    {
      id == 1 &&
        payment_method_id !== 1 &&
        active &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "4",
            label: (
              <div
                onClick={() => {
                  setPaymentId(String(paymentid))
                  setOpenResetConfirmation(true)
                }}
              >
                {" "}
                Reset
              </div>
            ),
          },
        ])
    }
    {
      id == 4 &&
        active &&
        !paymentSuccessful &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "5",
            label: (
              <div
                onClick={() => {
                  setPaymentId(String(paymentid))
                  setOpenRetryConfirmation(true)
                }}
              >
                {" "}
                Retry
              </div>
            ),
          },
        ])
    }
    {
      ;(id == 4 || id == 5 || payment_method_id !== 1) &&
        active &&
        !isSystemItem &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "6",
            label: (
              <div
                onClick={() => {
                  setPaymentId(String(paymentid))
                  setOpenUpdateConfirmation(true)
                }}
              >
                {" "}
                Update
              </div>
            ),
          },
        ])
    }
    {
      id == 2 &&
        active &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "7",
            label: (
              <div
                onClick={() => {
                  setPaymentId(String(paymentid))
                  setOpenRefreshConfirmation(true)
                }}
              >
                {" "}
                Refresh
              </div>
            ),
          },
        ])
    }
    {
      ;(id == 4 || id == 5) &&
        active &&
        !isSystemItem &&
        !isPayout &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "8",
            label: (
              <div
                onClick={() => {
                  if (canDelete) {
                    setPaymentId(String(paymentid))
                    setOpenDeleteConfirmation(true)
                  } else {
                    toast.unauthorized()
                  }
                }}
              >
                Remove
              </div>
            ),
          },
        ])
    }
    {
      ;(id == 4 || id == 5) &&
        !active &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "9",
            label: (
              <div
                onClick={() => {
                  setPaymentId(String(paymentid))
                  setOpenRestoreConfirmation(true)
                }}
              >
                {" "}
                Restore
              </div>
            ),
          },
        ])
    }

    return arrayOptions
  }
  const allActionItems = [
    {
      key: "1",
      label: (
        <button
          className="w-full flex justify-start"
          onClick={() => {
            setOpenSelectAllConfirmation(true)
            setPaymentId(getIdFromKey(x || ""))
          }}
        >
          Select
        </button>
      ),
    },
    {
      key: "6",
      label: (
        <button
          className="w-full flex justify-start"
          onClick={() => {
            setOpenDeselectAllConfirmation(true)
            setPaymentId(getIdFromKey(x || ""))
          }}
        >
          Deselect
        </button>
      ),
    },
    {
      key: "2",
      label: (
        <div
          className="w-full flex justify-start"
          onClick={() => {
            setOpenReverseConfirmation(true)
            setPaymentId(getIdFromKey(x || ""))
          }}
        >
          {" "}
          Reverse
        </div>
      ),
    },
    {
      key: "3",
      label: (
        <div
          className="w-full flex justify-start"
          onClick={() => {
            setOpenUnreverseConfirmation(true)
            setPaymentId(getIdFromKey(x || ""))
          }}
        >
          {" "}
          Unreverse
        </div>
      ),
    },
    {
      key: "4",
      label: (
        <div
          className="w-full flex justify-start"
          onClick={() => {
            setOpenResetConfirmation(true)
            setPaymentId(getIdFromKey(x || ""))
          }}
        >
          {" "}
          Reset
        </div>
      ),
    },
    {
      key: "5",
      label: (
        <div
          className="w-full flex justify-start"
          onClick={() => {
            setOpenRefreshConfirmation(true)
            setPaymentId(getIdFromKey(x || ""))
          }}
        >
          {" "}
          Refresh
        </div>
      ),
    },
    {
      key: "7",
      label: (
        <div
          className="w-full flex justify-start"
          onClick={() => {
            setOpenRetryConfirmation(true)
            setPaymentId(getIdFromKey(x || ""))
          }}
        >
          {" "}
          Retry
        </div>
      ),
    },
    {
      key: "8",
      label: (
        <div
          className="w-full flex justify-start"
          onClick={() => {
            setOpenUpdateConfirmation(true)
            setPaymentId(getIdFromKey(x || ""))
          }}
        >
          {" "}
          Update
        </div>
      ),
    },
  ]

  const { mutate: handleExport, isLoading: isDownloading } = useMutation({
    mutationFn: () => {
      toast.success("Download started.")
      return paymentSchedulesService.downloadPayoutItems(
        getIdFromKey(x || ""),
        activeCurrency?.currency_code,
        search
      )
    },
    onSuccess: (blob) => {
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-")
      const description = (data?.data?.description || "payment-schedule")
        .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-")
        .trim()
      const currency = activeCurrency?.currency_code || "ngn"
      saveAs(blob, `${description}_${currency}_${timestamp}.xlsx`)
    },
    onError: onQueryError,
  })

  if (activeTab === 1)
    return (
      <StatutoryInfo
        setActiveTab={setActiveTab}
        onSuccess={() => {
          setActiveTab(0)
          setIsStatutorySetup(false)
        }}
      />
    )
  return (
    <div className="relative">
      <Spin
        spinning={isPaymentLoading}
        indicator={antIcon}
      >
        <div className=" flex overflow-x-auto scrollbar-hide  mb-4 gap-[11px] justify-start">
          {cards.map((card, index) => (
            <div
              key={index}
              className={index > 2 ? "hidden lg:flex" : "flex w-full"}
            >
              <PayoutCard
                id={card.id}
                key={index}
                title={card.title}
                value={card.value}
                icon={card.icon}
                type={type == card.typeId ? true : false}
                handleClick={card.handleClick}
                currency={activeCurrency?.currency_symbol}
              />
            </div>
          ))}
        </div>
      </Spin>
      <div className=" bg-white rounded-5px mt-4 ">
        <div className=" flex items-center justify-between py-[10px] px-4">
          <div className="flex  gap-2 items-center  ">
            <div className="w-[268px] h-9">
              <Input
                placeholder="Search"
                prefix={
                  <MagnifyingGlassIcon className="w-4 h-4 text-neutral-300" />
                }
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="font-avenir "
              />
            </div>
          </div>

          <div className="flex  items-center gap-3">
            <ResponsiveActinBtn
              items={[
                {
                  key: "process",
                  label: (
                    <button
                      className="w-full flex justify-start"
                      onClick={() => {
                        setProcessSelected(true)
                      }}
                      disabled={
                        data?.data?.has_selected_unprocessed_payments
                          ? false
                          : true
                      }
                    >
                      Process selected payments{" "}
                    </button>
                  ),
                },

                ...allActionItems,
              ]}
            >
              <Dropdown
                menu={{
                  items: allActionItems,
                }}
              >
                <button className="h-9 border border-neutral100 bg-white inline-flex items-center justify-between gap-2 px-4 rounded-5px min-w-[190px] whitespace-nowrap text-padeBlack text-sm font-avenir font-medium">
                  Batch payout actions
                  <ChevronDownIcon className="text-black w-4 h-4 shrink-0" />
                </button>
              </Dropdown>
            </ResponsiveActinBtn>
            {user?.organisationPlan === "Custom" &&
            user?.customPlanPayrollBilling ? null : (
              <Button
                title="download"
                color="primary"
                submitting={isDownloading}
                disabled={!x || !activeCurrency || isDownloading}
                onClick={() => handleExport()}
              >
                Download payment file
              </Button>
            )}
          </div>
        </div>
        <div className="border relative mb-10">
          <SelectedTable
            payoutData={payoutData!}
            TableColumns={TableColumns}
            TableData={payoutData?.data?.docs || []}
            rowKey={(record) => record.payment_schedule_item_id}
            isPaymentLoading={
              isPayoutLoading ||
              isSubmittingLoading ||
              isSubmittingDeselectionLoading
            }
            submitSelection={submitSelection}
            submitDeselection={submitDeselection}
            setAllSelectedRowSum={setAllSelectedRowSum}
            setFilteredData={setFilteredData}
            setPagination={(update: any) =>
              handlePaginationChange(update.page, update.limit)
            }
          />
        </div>
      </div>

      {processSelected && (
        <ProcessPayment
          open={processSelected}
          onClose={() => {
            setProcessSelected(false)
          }}
          activeCurrency={activeCurrency}
          invalidateQueries={invalidateQueries}
        />
      )}
      {activeReference && (
        <ViewTransferDetails
          reference={activeReference}
          onClose={() => {
            setActiveReference("")
            setShowDetails(false)
          }}
          open={showDetails}
          showFee={false}
        />
      )}
      {openDownloadConfirmation && (
        <DownloadPayment
          open={openDownloadConfirmation}
          onClose={() => {
            setOpenDownloadConfirmation(false)
          }}
          data={data}
        />
      )}
      {/* VIEW */}
      <ViewPayoutModal
        open={showViewPayout}
        onClose={() => {
          setActive(null)
          setShowViewPayout(false)
        }}
        payment={active}
      />
      <ReversePayment
        open={openReverseConfirmation}
        onClose={() => {
          setOpenReverseConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        invalidateQueries={invalidateQueries}
        x={x || ""}
        currency={activeCurrency?.currency_code}
      />
      <UnreversePayment
        open={openUnreverseConfirmation}
        onClose={() => {
          setOpenUnreverseConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        invalidateQueries={invalidateQueries}
        x={x || ""}
        currency={activeCurrency?.currency_code}
      />
      <ResetPayment
        open={openResetConfirmation}
        onClose={() => {
          setOpenResetConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        invalidateQueries={invalidateQueries}
        x={x || ""}
        currency={activeCurrency?.currency_code}
      />

      <UpdatePayment
        open={openUpdateConfirmation}
        onClose={() => {
          setOpenUpdateConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        x={x || ""}
        invalidateQueries={invalidateQueries}
        currency={activeCurrency?.currency_code}
      />

      <RetryPayment
        open={openRetryConfirmation}
        onClose={() => {
          setOpenRetryConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        x={x || ""}
        invalidateQueries={invalidateQueries}
        currency={activeCurrency?.currency_code}
      />
      <RefreshPayment
        open={openRefreshConfirmation}
        onClose={() => {
          setOpenRefreshConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        x={x || ""}
        invalidateQueries={invalidateQueries}
        currency={activeCurrency?.currency_code}
      />

      <DeletePayment
        open={openDeleteConfirmation}
        onClose={() => {
          setOpenDeleteConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        invalidateQueries={invalidateQueries}
        // currency={activeCurrency?.currency_code}
      />

      <RestorePayment
        open={openRestoreConfirmation}
        onClose={() => {
          setOpenRestoreConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        invalidateQueries={invalidateQueries}
        // currency={activeCurrency?.currency_code}
      />
      <SelectPayment
        open={openSelectAllConfirmation}
        onClose={() => {
          setOpenSelectAllConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        x={x || ""}
        invalidateQueries={invalidateQueries}
        currency={activeCurrency?.currency_code}
      />
      <DeselectPayment
        open={openDeselectAllConfirmation}
        onClose={() => {
          setOpenDeselectAllConfirmation(false)
          setPaymentId(getIdFromKey(x || ""))
        }}
        paymentId={paymentId}
        x={x || ""}
        invalidateQueries={invalidateQueries}
        currency={activeCurrency?.currency_code}
      />
      <CustomModal
        destroyOnClose
        open={showApprovers}
        onCancel={() => {
          setShowApprovers(false)
        }}
        title="Approval Workflow Reviews"
      >
        <div className="flex flex-col items-center justify-start w-full  gap-2 min-h-[250px] max-h-[362px] overflow-y-auto">
          {approversData?.data?.reviews?.map((item, index) => (
            <div
              key={index}
              className="border py-[21px] px-[18px] border-[#C2C7D0] rounded w-full flex items-center justify-start"
            >
              <p className=" font-circular font-medium">{item?.description}</p>
            </div>
          ))}
        </div>
      </CustomModal>

      {/* FOOTER */}
      <div className="fixed bottom-0 flex justify-end left-0 right-0 h-[64px] gap-4  py-3 px-16 bg-white ">
        {(activeCurrency?.currency_code === "NGN" ||
          activeCurrency?.currency_code === "GHS" ||
          activeCurrency?.currency_code === "KES") &&
          data?.data?.payment_schedule_type_id === 1 && (
            <Button
              color="secondary"
              submitting={isPaymentLoading || isLoadingSetupInfo}
              disabled={isLoadingSetupInfo}
              onClick={() => {
                if (isLoadingSetupInfo) return
                if (isWalletSetup?.data?.isStatutoryInfoSetup) {
                  navigate(
                    `/dashboard/payment-schedules/statustory?x=${x}&s=${
                      activeCurrency?.currency_code ?? ""
                    }`,
                    {
                      state: {
                        title: data?.data?.description,
                        currency_code: activeCurrency?.currency_code,
                      },
                    }
                  )
                  return
                }
                setIsStatutorySetup(true)
              }}
            >
              Statutory Payments
            </Button>
          )}
        {data?.data?.payment_schedule_type_id !== 1 &&
          approversData &&
          approversData?.data?.reviews?.length > 0 && (
            <Button
              color="secondary"
              disabled={isLoadingNotes}
              onClick={() => {
                setShowApprovers(true)
              }}
            >
              View Approvals
            </Button>
          )}
        <Button
          color="primary"
          onClick={() => {
            setProcessSelected(true)
          }}
          submitting={isPaymentLoading}
          disabled={
            data?.data?.has_selected_unprocessed_payments ? false : true
          }
        >
          Process Selected Payments
        </Button>
      </div>
      <CustomModal
        open={isStatutorySetup}
        title={"Provide your Company Statutory Information"}
        onCancel={() => setIsStatutorySetup(false)}
        footer={
          <div className="flex justify-end items-center">
            <Button
              color="secondary"
              onClick={() => setIsStatutorySetup(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => setActiveTab(1)}
            >
              Continue
            </Button>
          </div>
        }
      >
        <div className="w-full">
          <P2 className=" mb-6 font-normal font-circular text-neutral500">
            To proceed, provide your company statutory details.
          </P2>
        </div>
      </CustomModal>
    </div>
  )
}

export default ViewCurrency
type Key = string | number | symbol | bigint
